import type { EngineOptions, System } from "./types";
import { World } from "./world";
import { Input } from "./input";

export class MapleEngine {
  private canvas: HTMLCanvasElement;
  private appId?: string;
  private targetFPS: number;
  private systems: System[] = [];
  private running = false;
  private rafId: number | null = null;
  private lastTime = 0;
  private accumulatorMs = 0;
  private readonly maxAccumulationMs = 1000; // avoid spiral of death

  readonly world: World;
  readonly input: Input;

  // ADDED: cached 2D context
  private ctx2d: CanvasRenderingContext2D | null = null;

  constructor(options: EngineOptions) {
    this.canvas = options.canvas;
    this.appId = options.appId;
    this.targetFPS = options.targetFPS ?? 60;
    this.world = new World();
    this.input = new Input();
  }

  addSystem(system: System): void {
    this.systems.push(system);
  }

  removeSystem(system: System): void {
    const idx = this.systems.indexOf(system);
    if (idx >= 0) this.systems.splice(idx, 1);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulatorMs = 0;
    this.input.attach(window);
    const loop = () => {
      if (!this.running) return;
      const now = performance.now();
      const elapsed = now - this.lastTime;
      this.lastTime = now;

      // Cap accumulation to prevent runaway
      this.accumulatorMs += Math.min(elapsed, this.maxAccumulationMs);

      const stepMs = 1000 / this.targetFPS;
      const stepSec = stepMs / 1000;

      // Fixed timestep updates
      while (this.accumulatorMs >= stepMs) {
        for (const system of this.systems) {
          system.update(stepSec);
        }
        this.accumulatorMs -= stepMs;
      }

      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.input.detach(window);

    // Actively clear and drop cached context
    this.clearCanvas();
    this.ctx2d = null;

    // ADDED: hard reset the drawing buffer so the last frame can't bleed into other canvases
    this.resetCanvasBuffer();
  }

  // ADDED: accessors for canvas and 2D context
  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  // ADDED: simple getter referencing appId so it's considered used by TS
  getAppId(): string | undefined {
    return this.appId;
  }

  getContext2D(): CanvasRenderingContext2D {
    if (this.ctx2d) return this.ctx2d;
    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      throw new Error("2D context is not available on this canvas.");
    }
    this.ctx2d = ctx;
    return ctx;
  }

  // ADDED: helper to clear canvas safely — only if a 2D context was already created
  clearCanvas(): void {
    if (this.ctx2d) {
      this.ctx2d.save();
      this.ctx2d.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx2d.restore();
    }
  }

  // ADDED: helper to hard-reset the canvas buffer
  private resetCanvasBuffer(): void {
    // Setting width/height resets the internal drawing buffer
    this.canvas.width = 0;
    this.canvas.height = 0;
  }
}